// src/pages/QuotesManagerPage.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Container, Table, Button, Form, Row, Col, Badge, Modal,
  Spinner, Alert, Accordion, Dropdown, ListGroup, ButtonGroup
} from "react-bootstrap";
import { callXeroFunction } from "../firebase.js";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { t } from "../utils/translations.js";
import LoadingSpinner from "../components/common/LoadingSpinner.js";

const STATUS_BADGES = {
  DRAFT: "secondary",
  SENT: "primary",
  ACCEPTED: "success",
  DECLINED: "danger",
  DELETED: "dark"
};

const EMPTY_LINE_ITEM = { description: "", quantity: 1, unitAmount: "" };

const getToday = () => new Date().toISOString().split("T")[0];
const getExpiry = (date) => {
  const d = new Date(date + "T00:00:00");
  d.setDate(d.getDate() + 30);
  return d.toISOString().split("T")[0];
};

const GST_RATE = 0.15;

const initialFormState = () => ({
  contactName: "",
  contactId: null,
  contactEmail: "",
  contactPhone: "",
  contactAddress: "",
  isNewClient: false,
  lineItems: [{ ...EMPTY_LINE_ITEM }],
  date: getToday(),
  expiryDate: getExpiry(getToday()),
  title: "",
  reference: "",
  terms: "",
  summary: "",
  status: "DRAFT",
  includeGST: true
});

export default function QuotesManagerPage() {
  const { user } = useAuth();
  const { language } = useLanguage();

  // Data state
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [editingQuoteId, setEditingQuoteId] = useState(null);
  const [formData, setFormData] = useState(initialFormState());
  const [saving, setSaving] = useState(false);

  // Client search state
  const [clientSearch, setClientSearch] = useState("");
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientSearchRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Action loading states
  const [actionLoading, setActionLoading] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Load quotes
  const loadQuotes = useCallback(async () => {
    try {
      setError(null);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const result = await callXeroFunction("getXeroQuotes", params, { method: "GET" });
      setQuotes(result.quotes || []);
    } catch (err) {
      console.error("Error loading quotes:", err);
      setError(t(language, "quotesErrorLoading"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, language]);

  // Load clients for search
  const loadClients = useCallback(async () => {
    try {
      const result = await callXeroFunction("getClients", {}, { method: "GET" });
      setClients(result.clients || []);
    } catch (err) {
      console.error("Error loading clients:", err);
    }
  }, []);

  useEffect(() => { loadQuotes(); }, [loadQuotes]);
  useEffect(() => { loadClients(); }, [loadClients]);

  // Close client dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(e.target)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filtered clients for autocomplete
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return [];
    const term = clientSearch.toLowerCase();
    return clients
      .filter(c => c.active !== false && c.name && c.name.toLowerCase().includes(term))
      .slice(0, 8);
  }, [clients, clientSearch]);

  // Filtered quotes for table
  const filteredQuotes = useMemo(() => {
    if (!searchTerm.trim()) return quotes;
    const term = searchTerm.toLowerCase();
    return quotes.filter(q =>
      (q.contact?.name || "").toLowerCase().includes(term) ||
      (q.quoteNumber || "").toLowerCase().includes(term) ||
      (q.title || "").toLowerCase().includes(term)
    );
  }, [quotes, searchTerm]);

  // Calculated totals for form
  const { subTotal, gstAmount, formTotal } = useMemo(() => {
    const sub = formData.lineItems.reduce((sum, item) => {
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unitAmount) || 0);
    }, 0);
    const gst = formData.includeGST ? sub * GST_RATE : 0;
    return { subTotal: sub, gstAmount: gst, formTotal: sub + gst };
  }, [formData.lineItems, formData.includeGST]);

  // Handlers
  const showSuccess = useCallback((msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }, []);

  const openCreateModal = useCallback(() => {
    setFormData(initialFormState());
    setClientSearch("");
    setShowClientDropdown(false);
    setModalMode("create");
    setEditingQuoteId(null);
    setShowModal(true);
  }, []);

  const openEditModal = useCallback((quote) => {
    setFormData({
      contactName: quote.contact?.name || "",
      contactId: quote.contact?.contactId || null,
      contactEmail: "",
      contactPhone: "",
      contactAddress: "",
      isNewClient: false,
      lineItems: (quote.lineItems || []).map(li => ({
        description: li.description || "",
        quantity: li.quantity || 1,
        unitAmount: li.unitAmount || 0
      })),
      date: (quote.date || "").split("T")[0] || getToday(),
      expiryDate: (quote.expiryDate || "").split("T")[0] || getExpiry((quote.date || "").split("T")[0] || getToday()),
      title: quote.title || "",
      reference: quote.reference || "",
      terms: quote.terms || "",
      summary: quote.summary || "",
      status: quote.status || "DRAFT",
      includeGST: quote.lineAmountTypes !== "NoTax"
    });
    setClientSearch(quote.contact?.name || "");
    setShowClientDropdown(false);
    setModalMode("edit");
    setEditingQuoteId(quote.quoteId);
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingQuoteId(null);
  }, []);

  const updateField = useCallback((field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === "date" && value) {
        updated.expiryDate = getExpiry(value);
      }
      return updated;
    });
  }, []);

  const handleClientSearchChange = useCallback((value) => {
    setClientSearch(value);
    setFormData(prev => ({
      ...prev,
      contactName: value,
      contactId: null,
      isNewClient: false
    }));
    // Debounce dropdown
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setShowClientDropdown(value.trim().length >= 2);
    }, 200);
  }, []);

  const selectClient = useCallback((client) => {
    setClientSearch(client.name);
    setFormData(prev => ({
      ...prev,
      contactName: client.name,
      contactId: client.xero_contact_id || null,
      contactEmail: client.email || "",
      contactPhone: client.phone || "",
      contactAddress: client.address || "",
      isNewClient: false
    }));
    setShowClientDropdown(false);
  }, []);

  const markAsNewClient = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      contactName: clientSearch.trim(),
      contactId: null,
      isNewClient: true
    }));
    setShowClientDropdown(false);
  }, [clientSearch]);

  const updateLineItem = useCallback((index, field, value) => {
    setFormData(prev => {
      const items = [...prev.lineItems];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, lineItems: items };
    });
  }, []);

  const addLineItem = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { ...EMPTY_LINE_ITEM }]
    }));
  }, []);

  const removeLineItem = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  }, []);

  const handleSave = async () => {
    // Validation
    if (!formData.contactName.trim()) {
      alert(language === "en" ? "Client name is required" : "Nome do cliente é obrigatório");
      return;
    }
    if (!formData.date) {
      alert(language === "en" ? "Date is required" : "Data é obrigatória");
      return;
    }
    const validItems = formData.lineItems.filter(li => li.description.trim() && parseFloat(li.unitAmount) > 0);
    if (validItems.length === 0) {
      alert(language === "en" ? "At least one item with description and price is required" : "Pelo menos um item com descrição e valor é obrigatório");
      return;
    }

    setSaving(true);
    try {
      const body = {
        lineItems: validItems.map(li => ({
          description: li.description.trim(),
          quantity: parseFloat(li.quantity) || 1,
          unitAmount: parseFloat(li.unitAmount)
        })),
        date: formData.date,
        expiryDate: formData.expiryDate || getExpiry(formData.date),
        status: formData.status,
        lineAmountTypes: formData.includeGST ? "Exclusive" : "NoTax"
      };

      // Use contactId if we have one (existing client), otherwise contactName
      if (formData.contactId) {
        body.contactId = formData.contactId;
      } else {
        body.contactName = formData.contactName.trim();
        if (formData.contactEmail) body.contactEmail = formData.contactEmail;
        if (formData.contactPhone) body.contactPhone = formData.contactPhone;
        if (formData.contactAddress) body.contactAddress = formData.contactAddress;
      }

      // Optional fields
      if (formData.title.trim()) body.title = formData.title.trim();
      if (formData.reference.trim()) body.reference = formData.reference.trim();
      if (formData.terms.trim()) body.terms = formData.terms.trim();
      if (formData.summary.trim()) body.summary = formData.summary.trim();

      let quoteId = editingQuoteId;

      if (modalMode === "create") {
        const result = await callXeroFunction("createXeroQuote", body, { method: "POST" });
        quoteId = result.quote?.quoteId || result.quoteId;
        showSuccess(t(language, "quotesCreated"));
      } else {
        await callXeroFunction("updateXeroQuote", body, { method: "PUT", id: editingQuoteId });
        showSuccess(t(language, "quotesUpdated"));
      }

      // If status is SENT, send email to client via Xero
      if (formData.status === "SENT" && quoteId) {
        try {
          await callXeroFunction("sendXeroQuoteEmail", {}, { method: "POST", id: quoteId, action: "email" });
          showSuccess(t(language, "quotesEmailSent"));
        } catch (emailErr) {
          console.error("Quote saved but email failed:", emailErr);
          alert(language === "en"
            ? "Quote saved but email could not be sent. Try sending manually from the actions menu."
            : "Orçamento salvo mas email não pôde ser enviado. Tente enviar manualmente pelo menu de ações.");
        }
      }

      closeModal();
      await loadQuotes();
    } catch (err) {
      console.error("Error saving quote:", err);
      alert(modalMode === "create" ? t(language, "quotesErrorCreating") : t(language, "quotesErrorUpdating"));
    } finally {
      setSaving(false);
    }
  };

  const handleSendEmail = async (quoteId) => {
    setActionLoading(quoteId);
    try {
      await callXeroFunction("sendXeroQuoteEmail", {}, { method: "POST", id: quoteId, action: "email" });
      showSuccess(t(language, "quotesEmailSent"));
      await loadQuotes();
    } catch (err) {
      console.error("Error sending email:", err);
      alert(t(language, "quotesErrorEmail"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = async (quote) => {
    setActionLoading(quote.quoteId);
    try {
      const result = await callXeroFunction("getXeroQuotePdf", {}, { method: "GET", id: quote.quoteId, action: "pdf" });
      const byteChars = atob(result.base64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${quote.quoteNumber || "Quote"}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading PDF:", err);
      alert(t(language, "quotesErrorPdf"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm(t(language, "quotesConfirmDelete"))) return;
    setActionLoading(quoteId);
    try {
      await callXeroFunction("deleteXeroQuote", {}, { method: "DELETE", id: quoteId });
      showSuccess(t(language, "quotesDeleted"));
      await loadQuotes();
    } catch (err) {
      console.error("Error deleting quote:", err);
      alert(t(language, "quotesErrorDeleting"));
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    // Handle various date formats from Xero (YYYY-MM-DD, YYYY-MM-DDT00:00:00, etc.)
    const clean = String(dateStr).split("T")[0];
    const d = new Date(clean + "T00:00:00");
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString(language === "pt" ? "pt-BR" : "en-NZ", {
      day: "2-digit", month: "2-digit", year: "numeric"
    });
  };

  // Auth check (after all hooks)
  if (!user?.isAdmin) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{language === "en" ? "Access denied" : "Acesso negado"}</Alert>
      </Container>
    );
  }

  if (loading) return <LoadingSpinner fullPage />;

  const isDraft = formData.status === "DRAFT";
  const saveBtnText = isDraft
    ? (language === "en" ? "Save Draft" : "Salvar Rascunho")
    : (language === "en" ? "Send Quote" : "Enviar Orçamento");
  const savingText = isDraft
    ? (language === "en" ? "Saving..." : "Salvando...")
    : (language === "en" ? "Sending..." : "Enviando...");

  return (
    <Container className="py-4" style={{ maxWidth: 1200 }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">{t(language, "quotesTitle")}</h3>
        <Button variant="primary" onClick={openCreateModal}>
          + {t(language, "quotesNew")}
        </Button>
      </div>

      {successMsg && (
        <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Row className="mb-3 g-2">
        <Col xs={12} md={4}>
          <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t(language, "quotesFilterAll")}</option>
            <option value="DRAFT">Draft</option>
            <option value="SENT">Sent</option>
            <option value="ACCEPTED">Accepted</option>
            <option value="DECLINED">Declined</option>
          </Form.Select>
        </Col>
        <Col xs={12} md={8}>
          <Form.Control
            type="text"
            placeholder={t(language, "quotesSearch")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
      </Row>

      {/* Table */}
      {filteredQuotes.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <h1 className="mb-3">📝</h1>
          <h5>{t(language, "quotesEmpty")}</h5>
          <p>{t(language, "quotesEmptyDesc")}</p>
        </div>
      ) : (
        <Table hover responsive className="align-middle">
          <thead className="table-light">
            <tr>
              <th>{t(language, "quotesNumber")}</th>
              <th>{t(language, "quotesClient")}</th>
              <th>{t(language, "quotesDate")}</th>
              <th>{t(language, "quotesExpiry")}</th>
              <th className="text-end">{t(language, "quotesTotal")}</th>
              <th className="text-center">{t(language, "quotesStatus")}</th>
              <th className="text-end">{t(language, "quotesActions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuotes.map(quote => (
              <tr key={quote.quoteId}>
                <td className="fw-bold">{quote.quoteNumber || "-"}</td>
                <td>{quote.contact?.name || "-"}</td>
                <td>{formatDate(quote.date)}</td>
                <td>{formatDate(quote.expiryDate)}</td>
                <td className="text-end">
                  ${parseFloat(quote.total || 0).toFixed(2)}
                  {quote.totalTax > 0 && (
                    <div className="text-muted small" style={{ fontSize: "0.75em" }}>
                      {language === "en" ? "incl." : "incl."} GST ${parseFloat(quote.totalTax).toFixed(2)}
                    </div>
                  )}
                </td>
                <td className="text-center">
                  <Badge bg={STATUS_BADGES[quote.status] || "secondary"}>
                    {quote.status}
                  </Badge>
                </td>
                <td className="text-end">
                  {actionLoading === quote.quoteId ? (
                    <Spinner size="sm" />
                  ) : (
                    <Dropdown align="end">
                      <Dropdown.Toggle variant="outline-secondary" size="sm">
                        {t(language, "quotesActions")}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => openEditModal(quote)}>
                          {t(language, "edit")}
                        </Dropdown.Item>
                        {(quote.status === "DRAFT" || quote.status === "SENT") && (
                          <Dropdown.Item onClick={() => handleSendEmail(quote.quoteId)}>
                            {t(language, "quotesSendEmail")}
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item onClick={() => handleDownloadPdf(quote)}>
                          {t(language, "quotesDownloadPdf")}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item className="text-danger" onClick={() => handleDelete(quote.quoteId)}>
                          {t(language, "delete")}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={closeModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === "create" ? t(language, "quotesNew") : t(language, "quotesEdit")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Client Search */}
          <Form.Group className="mb-3" ref={clientSearchRef}>
            <Form.Label className="fw-bold">{t(language, "quotesClientName")}</Form.Label>
            <Form.Control
              type="text"
              value={clientSearch}
              onChange={(e) => handleClientSearchChange(e.target.value)}
              onFocus={() => { if (clientSearch.trim().length >= 2) setShowClientDropdown(true); }}
              placeholder={language === "en" ? "Search existing client or type new name..." : "Buscar cliente existente ou digitar novo nome..."}
              autoComplete="off"
            />

            {/* Client autocomplete dropdown */}
            {showClientDropdown && (
              <ListGroup className="position-absolute shadow-sm" style={{ zIndex: 1050, maxWidth: "calc(100% - 2rem)" }}>
                {filteredClients.map(client => (
                  <ListGroup.Item
                    key={client.id}
                    action
                    onClick={() => selectClient(client)}
                    className="py-2"
                  >
                    <div className="fw-bold">{client.name}</div>
                    {(client.email || client.phone) && (
                      <small className="text-muted">
                        {client.email}{client.email && client.phone ? " | " : ""}{client.phone}
                      </small>
                    )}
                  </ListGroup.Item>
                ))}
                {filteredClients.length === 0 && clientSearch.trim().length >= 2 && (
                  <ListGroup.Item action onClick={markAsNewClient} className="py-2 text-primary">
                    <strong>+ {language === "en" ? "Create new client" : "Cadastrar novo cliente"}: "{clientSearch.trim()}"</strong>
                  </ListGroup.Item>
                )}
                {filteredClients.length > 0 && (
                  <ListGroup.Item action onClick={markAsNewClient} className="py-2 text-muted border-top">
                    <small>+ {language === "en" ? "Not in the list? Create new client" : "Não está na lista? Cadastrar novo cliente"}</small>
                  </ListGroup.Item>
                )}
              </ListGroup>
            )}

            {/* Selected client info */}
            {formData.contactId && (
              <small className="text-success mt-1 d-block">
                {language === "en" ? "Existing client selected" : "Cliente existente selecionado"}
              </small>
            )}
          </Form.Group>

          {/* New client fields */}
          {formData.isNewClient && (
            <div className="p-3 mb-3 bg-light rounded border">
              <small className="fw-bold text-primary d-block mb-2">
                {language === "en" ? "New client details (optional)" : "Dados do novo cliente (opcional)"}
              </small>
              <Row className="g-2">
                <Col xs={12} md={4}>
                  <Form.Control
                    type="email"
                    placeholder="Email"
                    value={formData.contactEmail}
                    onChange={(e) => updateField("contactEmail", e.target.value)}
                    size="sm"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Form.Control
                    type="text"
                    placeholder={language === "en" ? "Phone" : "Telefone"}
                    value={formData.contactPhone}
                    onChange={(e) => updateField("contactPhone", e.target.value)}
                    size="sm"
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Form.Control
                    type="text"
                    placeholder={language === "en" ? "Address" : "Endereço"}
                    value={formData.contactAddress}
                    onChange={(e) => updateField("contactAddress", e.target.value)}
                    size="sm"
                  />
                </Col>
              </Row>
            </div>
          )}

          {/* Line Items */}
          <Form.Label className="fw-bold mb-2">{language === "en" ? "Items" : "Itens"}</Form.Label>
          {formData.lineItems.map((item, index) => (
            <Row key={index} className="mb-2 g-2 align-items-end">
              <Col xs={12} md={5}>
                {index === 0 && <Form.Label className="small text-muted">{t(language, "quotesDescription")}</Form.Label>}
                <Form.Control
                  type="text"
                  value={item.description}
                  onChange={(e) => updateLineItem(index, "description", e.target.value)}
                  placeholder={language === "en" ? "Service description" : "Descrição do serviço"}
                />
              </Col>
              <Col xs={4} md={2}>
                {index === 0 && <Form.Label className="small text-muted">{t(language, "quotesQuantity")}</Form.Label>}
                <Form.Control
                  type="number"
                  min="1"
                  step="1"
                  value={item.quantity}
                  onChange={(e) => updateLineItem(index, "quantity", e.target.value)}
                />
              </Col>
              <Col xs={5} md={3}>
                {index === 0 && <Form.Label className="small text-muted">{t(language, "quotesUnitPrice")}</Form.Label>}
                <div className="input-group">
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitAmount}
                    onChange={(e) => updateLineItem(index, "unitAmount", e.target.value)}
                  />
                </div>
              </Col>
              <Col xs={3} md={2}>
                {formData.lineItems.length > 1 && (
                  <Button variant="outline-danger" size="sm" className="w-100" onClick={() => removeLineItem(index)}>
                    {t(language, "quotesRemoveItem")}
                  </Button>
                )}
              </Col>
            </Row>
          ))}

          <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
            <Button variant="outline-primary" size="sm" onClick={addLineItem}>
              + {t(language, "quotesAddItem")}
            </Button>
            <div className="text-end">
              {formData.includeGST && (
                <>
                  <div className="text-muted small">Subtotal: ${subTotal.toFixed(2)}</div>
                  <div className="text-muted small">GST (15%): ${gstAmount.toFixed(2)}</div>
                </>
              )}
              <strong>{t(language, "quotesTotal")}: ${formTotal.toFixed(2)}</strong>
            </div>
          </div>

          {/* GST Toggle */}
          <Form.Check
            type="switch"
            id="gst-switch"
            label={language === "en" ? "Include GST (15%)" : "Incluir GST (15%)"}
            checked={formData.includeGST}
            onChange={(e) => updateField("includeGST", e.target.checked)}
            className="mb-3"
          />

          <hr />

          {/* Date & Expiry */}
          <Row className="mb-3 g-2">
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="fw-bold">{t(language, "quotesDate")} *</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.date}
                  onChange={(e) => updateField("date", e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="fw-bold">{t(language, "quotesExpiry")}</Form.Label>
                <Form.Control
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => updateField("expiryDate", e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Draft / Send toggle */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold d-block">
              {language === "en" ? "Action" : "Ação"}
            </Form.Label>
            <ButtonGroup className="w-100">
              <Button
                variant={isDraft ? "secondary" : "outline-secondary"}
                onClick={() => updateField("status", "DRAFT")}
              >
                {language === "en" ? "Save as Draft" : "Salvar como Rascunho"}
              </Button>
              <Button
                variant={!isDraft ? "primary" : "outline-primary"}
                onClick={() => updateField("status", "SENT")}
              >
                {language === "en" ? "Send to Client" : "Enviar ao Cliente"}
              </Button>
            </ButtonGroup>
          </Form.Group>

          {/* Optional Fields */}
          <Accordion className="mb-2">
            <Accordion.Item eventKey="0">
              <Accordion.Header>{t(language, "quotesOptionalFields")}</Accordion.Header>
              <Accordion.Body>
                <Form.Group className="mb-2">
                  <Form.Label className="small">{t(language, "quotesTitle2")}</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.title}
                    onChange={(e) => updateField("title", e.target.value)}
                    placeholder={language === "en" ? "Quote title" : "Título do orçamento"}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">{t(language, "quotesReference")}</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.reference}
                    onChange={(e) => updateField("reference", e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">{t(language, "quotesTerms")}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.terms}
                    onChange={(e) => updateField("terms", e.target.value)}
                    placeholder={language === "en" ? "e.g. Valid for 30 days" : "ex: Válido por 30 dias"}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label className="small">{t(language, "quotesSummary")}</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.summary}
                    onChange={(e) => updateField("summary", e.target.value)}
                  />
                </Form.Group>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            {t(language, "cancel")}
          </Button>
          <Button
            variant={isDraft ? "secondary" : "primary"}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <Spinner size="sm" className="me-1" /> : null}
            {saving ? savingText : saveBtnText}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
